import { EnergySource, HvacArea } from '@belimo-retrofit-portal/logic';
import { FieldPath } from '@form-ts/core';
import { useFormWatch } from '@form-ts/react';
import React, { memo } from 'react';
import { FormattedHTMLMessage, useIntl } from 'react-intl';
import { FormattedMessage } from 'src/migration/react-intl';
import { Button } from 'src/modules/common/components/Button';
import { CarbonIntensityUnit } from 'src/modules/common/components/CarbonIntensityUnit';
import { EnergySourceUnit } from 'src/modules/common/components/EnergySourceUnit';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { ENERGY_SOURCE_HEATING_OPTIONS } from 'src/modules/common/constants/options';
import { HeatingIcon } from 'src/modules/common/icons/HeatingIcon';
import { InfoIcon } from 'src/modules/common/icons/InfoIcon';
import { LightningIcon } from 'src/modules/common/icons/LightningIcon';
import { ResetIcon } from 'src/modules/common/icons/ResetIcon';
import { ThermostatIcon } from 'src/modules/common/icons/ThermostatIcon';
import { DecimalField } from 'src/modules/form/components/DecimalField';
import { DropdownField } from 'src/modules/form/components/DropdownField';
import { SwitchField } from 'src/modules/form/components/SwitchField';
import { FormError } from 'src/modules/form/types/FormError';
import { ensureNonNull } from 'src/modules/form/utils/transform';
import { HvacFormAreaData, HvacFormData } from 'src/modules/hvac/types/HvacFormData';

type Props = {
  readonly resetSection: (area: HvacArea) => void;
  readonly path: FieldPath<HvacFormData, FormError, HvacFormAreaData>;
  readonly readOnly: boolean;
};

export const HvacSettingsHeatingCardView = memo(({ path, readOnly, resetSection }: Props) => {
  const intl = useIntl();
  const enabled = useFormWatch(path.form, path.at('enabled').value.get);
  const energySource = useFormWatch(path.form, path.at('thermal').at('energySource').value.get);

  return (
    <div className="bp-hvac-settings-view__card">
      <div className="bp-hvac-settings-view__card-header">
        <div className="bp-hvac-settings-view__card-icon">
          <HeatingIcon/>
        </div>

        <div className="bp-hvac-settings-view__card-title">
          <FormattedMessage id="hvac/tab/heating"/>
        </div>

        {!readOnly && (
          <div className="bp-hvac-settings-view__card-actions">
            <SwitchField
              field={path.at('enabled')}
            />

            <Tooltip id={`bp-hvac-setting-view-${path.path}`} value={intl.formatMessage({ id: 'hvac/energy/reset' })}>
              <Button
                kind="ghost"
                size="sm"
                iconOnly={true}
                onClick={() => resetSection(HvacArea.HEATING)}
                icon={(
                  <div className="bp-hvac-settings-view__card-icon">
                    <ResetIcon/>
                  </div>
                )}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div className="bp-hvac-settings-view__card-section">
        <div className="bp-hvac-settings-view__card-description">
          <div className="bp-hvac-settings-view__card-icon">
            <ThermostatIcon/>
          </div>

          <FormattedMessage id="hvac/energy/heating"/>
        </div>

        <DropdownField
          size="md"
          label={intl.formatMessage({ id: 'hvac/energy/energySource' })}
          field={path.at('thermal').at('energySource').transform(ensureNonNull())}
          options={ENERGY_SOURCE_HEATING_OPTIONS}
          renderOption={renderEnergySourceOption}
          disabled={!enabled}
          readOnly={readOnly}
        />

        <DecimalField
          size="md"
          label={intl.formatMessage({ id: 'hvac/energy/annualEnergyUse' })}
          field={path.at('thermal').at('annualEnergyUse')}
          disabled={!enabled}
          actions={<EnergySourceUnit source={energySource}/>}
          readOnly={readOnly}
        />

        <DecimalField
          size="md"
          label={(
            <div className="bp-hvac-settings-view__card-energy-electrical-label">
              <FormattedMessage id="hvac/energy/carbonIntensity"/>

              <Tooltip
                place="top"
                id="bp-hvac-settings-heating-thermal-carbon-intensity"
                value={(
                  <div className="bp-hvac-settings-view__tooltip">
                    <FormattedHTMLMessage id="hvac/energy/carbonIntensity/tooltip"/>
                  </div>
                )}
              >
                <div
                  className="bp-hvac-settings-view__tooltip-icon"
                >
                  <InfoIcon/>
                </div>
              </Tooltip>
            </div>
          )}
          field={path.at('thermal').at('carbonIntensity')}
          disabled={!enabled}
          readOnly={readOnly}
          actions={<CarbonIntensityUnit source={energySource}/>}
        />
      </div>

      <div className="bp-hvac-settings-view__card-section">
        <div className="bp-hvac-settings-view__card-description">
          <div className="bp-hvac-settings-view__card-icon">
            <LightningIcon/>
          </div>

          <FormattedMessage id="hvac/energy/electricalEnergy/heating"/>
        </div>

        <DecimalField
          size="md"
          label={(
            <div className="bp-hvac-settings-view__card-energy-electrical-label">
              <FormattedMessage id="hvac/energy/annualEnergyUse"/>

              <Tooltip
                place="top"
                id="bp-hvac-settings-heating-electrical"
                value={(
                  <div className="bp-hvac-settings-view__tooltip">
                    <FormattedMessage id="hvac/energy/electricalEnergy/heating/tooltip"/>
                  </div>
                )}
              >
                <div
                  className="bp-hvac-settings-view__tooltip-icon"
                >
                  <InfoIcon/>
                </div>
              </Tooltip>
            </div>
          )}
          field={path.at('electrical').at('annualEnergyUse')}
          disabled={!enabled}
          readOnly={readOnly}
          autoComplete="off"
          actions={<EnergySourceUnit source={EnergySource.ELECTRICITY}/>}
        />

        <DecimalField
          size="md"
          label={(
            <div className="bp-hvac-settings-view__card-energy-electrical-label">
              <FormattedMessage id="hvac/energy/carbonIntensity"/>

              <Tooltip
                place="top"
                id="bp-hvac-settings-heating-electrical-carbon-intensity"
                value={(
                  <div className="bp-hvac-settings-view__tooltip">
                    <FormattedHTMLMessage id="hvac/energy/carbonIntensity/tooltip"/>
                  </div>
                )}
              >
                <div
                  className="bp-hvac-settings-view__tooltip-icon"
                >
                  <InfoIcon/>
                </div>
              </Tooltip>
            </div>
          )}
          field={path.at('electrical').at('carbonIntensity')}
          disabled={!enabled}
          readOnly={readOnly}
          autoComplete="off"
          actions={<CarbonIntensityUnit source={EnergySource.ELECTRICITY}/>}
        />
      </div>
    </div>
  );
});

function renderEnergySourceOption(option: EnergySource): React.ReactElement {
  return <FormattedMessage id={`common/energySource/${option}`}/>;
}
